/**
 * Company: "Achieve Possibility" — SplitType + GSAP ScrollTrigger timeline.
 * 0–40% scroll progress: 좌→우 글자 fill, 이후 배경 페이드인, 마지막에 설명 카피.
 * 모바일(lg 미만, matchMedia min-width 1024px 미만)에서는 ScrollTrigger·pin 없이 정적 표시.
 */
import SplitType from "split-type";

/** SCSS `$breakpoints.lg` 와 동일 */
const DESKTOP_MIN_PX = 1024;

type GsapLike = {
  registerPlugin: (p: unknown) => void;
  set: (t: unknown, v: Record<string, unknown>) => void;
  timeline: (vars?: Record<string, unknown>) => { kill: () => void };
};

function addMqlListener(mql: MediaQueryList, handler: () => void): void {
  if (typeof mql.addEventListener === "function") {
    mql.addEventListener("change", handler);
  } else {
    mql.addListener(handler);
  }
}

export function initCompanyPossibility(): void {
  const gsap = window.gsap as GsapLike | undefined;
  const ST = window.ScrollTrigger;
  if (!gsap || !ST) return;

  const root = document.querySelector<HTMLElement>("[data-bro-possibility]");
  if (!root) return;

  const title = root.querySelector<HTMLElement>(".bro-company__possibility-title");
  const bg = root.querySelector<HTMLElement>(".bro-company__possibility-bg");
  const copy = root.querySelector<HTMLElement>(".bro-company__possibility-copy");
  if (!title || !bg || !copy) return;

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reducedMotion) {
    gsap.set(bg, { opacity: 1 });
    gsap.set(copy, { opacity: 1, y: 0 });
    return;
  }

  const mqDesktop = window.matchMedia(`(min-width: ${DESKTOP_MIN_PX}px)`);

  let split: SplitType | null = null;
  let tl: ReturnType<GsapLike["timeline"]> | null = null;
  let resizeTimer: ReturnType<typeof setTimeout> | undefined;

  const teardown = (): void => {
    tl?.kill();
    tl = null;
    split?.revert();
    split = null;
  };

  /** 모바일: pin·scrub 없이 최종 상태(배경·카피·타이틀)만 표시 */
  const applyMobileStatic = (): void => {
    teardown();
    gsap.set(bg, { opacity: 1 });
    gsap.set(copy, { opacity: 1, y: 0 });
  };

  const buildDesktop = (): void => {
    teardown();
    gsap.registerPlugin(ST);

    split = new SplitType(title, { types: "chars", tagName: "span" });
    const chars = split.chars as HTMLElement[];
    if (!chars.length) return;

    gsap.set(bg, { opacity: 0 });
    gsap.set(copy, { opacity: 0, y: 28 });
    gsap.set(chars, { color: "rgba(255, 255, 255, 0.32)" });

    tl = gsap.timeline({
      scrollTrigger: {
        trigger: root,
        start: "top top",
        end: "+=300%",
        pin: true,
        scrub: 0.65,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      },
    });

    tl.to(
      chars,
      {
        color: "#ffffff",
        duration: 0.02,
        stagger: { amount: 0.4, from: "start" },
        ease: "none",
      },
      0
    );

    tl.to(bg, { opacity: 1, duration: 0.28, ease: "power2.out" }, 0.4);
    tl.to(copy, { opacity: 1, y: 0, duration: 0.3, ease: "power2.out" }, 0.62);
  };

  const sync = (): void => {
    if (mqDesktop.matches) {
      buildDesktop();
    } else {
      applyMobileStatic();
    }
  };

  sync();
  addMqlListener(mqDesktop, sync);

  window.addEventListener("resize", () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      sync();
      ST.refresh();
    }, 220);
  });
}
